import React from 'react';
import classNames from 'classnames';
import { isNil } from 'lodash';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives';
import { txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import { generatedTypesNew } from '@css-xp/graphtypes';
//@ts-ignore
import TermPaymentIcon from '../assets/img/icon-term-payment.svg';
import './term-payment-method.scss';
import { formatTermPaymentName } from './term-payment-utils';
import { FormattedCurrencyV2 } from '../formattedV2';
import { paymentTypeStyles } from './term-payment-method.styles';

export interface TermPaymentMethodProps {
  paymentNetType?: string;
  totalLimit?: number;
  currentTermAccountStatus?: generatedTypesNew.Maybe<string> | undefined;
  hasPayOnAccount?: boolean;
  version?: number;
  shouldHideVerticalDivider?: boolean;
}

const TermPaymentMethod = ({
  paymentNetType = 'NET30',
  totalLimit,
  currentTermAccountStatus,
  hasPayOnAccount,
  version,
  shouldHideVerticalDivider,
}: TermPaymentMethodProps) => {
  const paymentType = hasPayOnAccount ? 'Pay on account' : 'Pay later by invoice';
  const termPaymentName = formatTermPaymentName(paymentNetType);

  const renderLimit = () => {
    if (isNil(totalLimit)) return null;

    const limit = (
      <>
        <FormattedCurrencyV2 showCurrencyCode showCommas value={totalLimit} /> limit
      </>
    );

    if (version === 2) {
      return <Text color="color.text.subtle">{limit}</Text>;
    }

    return (
      <p className="term-payment-type" data-testid="term-payment-type">
        {limit}
      </p>
    );
  };

  const renderDeactivated = (): JSX.Element | null => {
    const isDeactivated = currentTermAccountStatus === txaPushPaymentStatus.INELIGIBLE;

    if (!isDeactivated) return null;

    const lozenge = <Lozenge appearance="removed">DEACTIVATED</Lozenge>;

    if (version === 2) {
      return lozenge;
    }

    return <span className="term-payment-type">{lozenge}</span>;
  };

  if (version === 2) {
    return (
      <Flex justifyContent="space-between" alignItems="center" gap="space.200" testId="term-payment-method-v2">
        {/* @ts-ignore */}
        <img src={TermPaymentIcon} height="30" width="45" />
        <Flex gap="space.100" alignItems="baseline">
          <Stack space="space.050">
            <Box paddingInlineEnd="space.100" xcss={shouldHideVerticalDivider ? {} : paymentTypeStyles}>
              <Text weight="bold">{paymentType}</Text>
            </Box>
            {renderLimit()}
          </Stack>
          <Text color="color.text.subtle" testId="term-payment-type">
            {termPaymentName}
          </Text>
          {renderDeactivated()}
        </Flex>
      </Flex>
    );
  }

  return (
    <div className="term-payment-wrapper">
      <div className="term-payment">
        <div className="term-payment-container">
          <div data-testid="term-payment" className="term-payment-content">
            <div className="term-payment-icon" data-testid="term-payment-icon">
              {/* @ts-ignore */}
              <img src={TermPaymentIcon} />
            </div>
            <div className={classNames('term-payment-details', { 'on-account': hasPayOnAccount })}>
              <div className="term-payment-header" data-testid="term-payment-header">
                {paymentType}
              </div>
              <p className="term-payment-type" data-testid="term-payment-type">
                {termPaymentName}
              </p>
              {renderLimit()}
              {renderDeactivated()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermPaymentMethod;
